import { useQuery } from "@tanstack/react-query"
import { getRouteApi } from "@tanstack/react-router"
import { Loader2, Search, Upload } from "lucide-react"
import { useRef, useState } from "react"

import { Button, Input } from "@atlant/ui"

import { graphql } from "#gql"
import { queryKeys } from "#lib/query-keys.ts"
import { FlightsAircraftSelect } from "#pages/authenticated/flights/components/toolbar/flights-aircraft-select.tsx"
import { useLoggedInUserInfo } from "#store/user-info.ts"
import { client } from "#utils/graphql-client.ts"

import { InvoiceUploadListDocument, UploadQueue } from "./anomaly/upload-queue.tsx"
import { INVOICE_UPLOAD_ACCEPT, useInvoiceUpload } from "./anomaly/use-invoice-upload.ts"
import { InvoiceTabs, type InvoiceTabItem } from "./components/invoice-tabs.tsx"
import { MonthGrid } from "./components/month-grid.tsx"
import { YearSelect } from "./components/year-select.tsx"
import { LateInvoices } from "./late/late-invoices.tsx"

export const InvoiceMonthSummariesDocument = graphql(/* GraphQL */ `
  query InvoiceMonthSummaries($input: QueryInvoiceMonthSummariesInput!) {
    invoiceMonthSummaries(input: $input) {
      month
      invoiceCount
      lateCount
      needsReviewCount
      totals {
        currency
        amount
      }
    }
  }
`)

const route = getRouteApi("/_authenticated/$slug/invoices/")

type InvoicesSearch = ReturnType<typeof route.useSearch>

export const Invoices = () => {
  const { me } = useLoggedInUserInfo()
  const { aircraftId, year, tab } = route.useSearch()
  const { slug } = route.useParams()
  const navigate = route.useNavigate()
  const [search, setSearch] = useState("")
  const fileInputRef = useRef<HTMLInputElement>(null)

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.invoices.monthSummaries(me.organization.id, aircraftId, year),
    queryFn: () =>
      client.request(InvoiceMonthSummariesDocument, {
        input: { organizationId: me.organization.id, aircraftId, year },
      }),
  })

  const summaries = data?.invoiceMonthSummaries ?? []
  const totalLate = summaries.reduce((sum, s) => sum + s.lateCount, 0)

  // Also feeds the Anomaly badge, so it loads on every tab
  const { data: uploadsData, isLoading: uploadsLoading } = useQuery({
    queryKey: queryKeys.invoices.uploads(me.organization.id),
    queryFn: () =>
      client.request(InvoiceUploadListDocument, {
        input: { organizationId: me.organization.id },
      }),
    // HANDLED BY SSE
    // // Classification runs in the background; poll until every upload has been through it
    // refetchInterval: ({ state }) =>
    //   state.data?.invoiceUploadList.some(({ status }) => status === "CLASSIFYING")
    //     ? 3000
    //     : false,
  })
  const uploads = uploadsData?.invoiceUploadList ?? []
  const awaitingReview = uploads.filter((u) => u.status === "AWAITING_REVIEW").length

  const updateSearch = (patch: Partial<InvoicesSearch>) =>
    // Router is not type-registered, so the reducer param needs an explicit type
    navigate({
      to: ".",
      search: (prev: InvoicesSearch) => ({ ...prev, ...patch }),
      replace: true,
    })

  const { upload, progress } = useInvoiceUpload({
    onDone: () => updateSearch({ tab: "anomaly" }),
  })

  const tabs: InvoiceTabItem[] = [
    { value: "all", label: "All" },
    { value: "late", label: "Late invoices", badge: totalLate || undefined },
    { value: "anomaly", label: "Anomaly", badge: awaitingReview || undefined },
  ]

  return (
    <div className="flex h-full w-full flex-col gap-4 p-4">
      <div className="flex items-center justify-between gap-4">
        <p className="text-muted-foreground text-sm">
          Invoices from imported monthly reports
        </p>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={INVOICE_UPLOAD_ACCEPT}
          className="hidden"
          onChange={(e) => {
            // Copy before clearing: picking the same files again must fire onChange
            const files = Array.from(e.target.files ?? [])
            e.target.value = ""
            void upload(files)
          }}
        />
        <Button
          onClick={() => fileInputRef.current?.click()}
          disabled={progress !== null}
        >
          {progress ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Upload className="size-4" />
          )}
          {progress ? `Uploading ${progress.done}/${progress.total}` : "Upload invoice"}
        </Button>
      </div>

      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          <FlightsAircraftSelect
            value={aircraftId ?? ""}
            onChange={(value) => updateSearch({ aircraftId: value || undefined })}
          />
          <YearSelect value={year} onChange={(value) => updateSearch({ year: value })} />
        </div>
        <div className="relative">
          <Search className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2" />
          <Input
            placeholder="Search invoices"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 w-56 pl-9 text-sm"
          />
        </div>
      </div>

      <InvoiceTabs
        tabs={tabs}
        value={tab}
        onChange={(value) => updateSearch({ tab: value })}
      />

      {/* -m-1 p-1: room for the card hover lift and focus ring, which overflow-auto would clip */}
      <div className="-m-1 flex-1 overflow-auto p-1">
        {tab === "all" ? (
          <MonthGrid
            slug={slug}
            year={year}
            aircraftId={aircraftId}
            summaries={summaries}
            isLoading={isLoading}
          />
        ) : tab === "late" ? (
          <LateInvoices
            year={year}
            aircraftId={aircraftId}
            search={search}
            summaries={summaries}
            summariesLoading={isLoading}
          />
        ) : (
          <UploadQueue uploads={uploads} isLoading={uploadsLoading} search={search} />
        )}
      </div>
    </div>
  )
}
